import { useEffect, useRef, useState } from "react";

import { Check, Loader2 } from "lucide-react";

import { Input } from "~/components/ui/input";
import { supabase } from "~/integrations/supabase/client";
import { cn } from "~/lib/utils";

interface ClientRow {
  id: string;
  name: string;
  tax_id: string | null;
}

interface ClientPickerProps {
  value: string;
  onChange: (id: string) => void;
}

// Dialog-safe client picker with its own precise server search.
//
// Renders a plain conditionally-mounted dropdown (no Radix Popover / cmdk, which
// don't show results inside a modal Dialog). Search matches ALL typed terms (AND)
// against name/RUT and orders by name, so "PRODUCTOS FERNANDEZ S.A" finds exactly
// that client. (The shared useClientSearch ORs terms together, so a common term
// like "s.a" floods the first 15 rows with unrelated "... S.A." companies and the
// real match never shows — that failure is avoided here.)
export function ClientPicker({ value, onChange }: ClientPickerProps) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [results, setResults] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const latestQuery = useRef("");

  // Debounced AND-of-terms search once at least 2 characters are typed.
  useEffect(() => {
    const q = term.trim();
    latestQuery.current = q;
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timeout = setTimeout(async () => {
      const terms = q
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.replace(/[,()%]/g, ""))
        .filter((t) => t.length >= 2);
      if (terms.length === 0) {
        if (latestQuery.current === q) {
          setResults([]);
          setLoading(false);
        }
        return;
      }
      let req = supabase
        .from("clients")
        .select("id, name, tax_id")
        .is("deleted_at", null);
      // Each term ANDed (repeated filters AND in PostgREST); each term may match
      // name OR tax_id. This narrows to rows containing every typed word.
      for (const t of terms) {
        req = req.or(`name.ilike.%${t}%,tax_id.ilike.%${t}%`);
      }
      const { data, error } = await req.order("name").limit(20);
      if (latestQuery.current !== q) return; // a newer query superseded this one
      if (!error) setResults((data as ClientRow[]) ?? []);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [term]);

  // Close the dropdown when clicking outside the picker.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const handleSelect = (client: ClientRow) => {
    onChange(client.id);
    setSelectedName(client.name);
    setTerm("");
    setResults([]);
    setOpen(false);
  };

  const showDropdown = open && term.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative">
      {selectedName && value ? (
        <div className="mb-1 truncate text-sm text-gray-700">
          Seleccionado: <span className="font-medium">{selectedName}</span>
        </div>
      ) : null}
      <Input
        className="h-9"
        placeholder="Buscar cliente por nombre o RUT..."
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {showDropdown ? (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full origin-top overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg duration-200 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
          {loading ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Buscando clientes...
            </div>
          ) : results.length > 0 ? (
            results.map((client) => (
              <button
                key={client.id}
                type="button"
                onClick={() => handleSelect(client)}
                className="flex w-full items-start gap-2 px-4 py-2 text-left text-sm hover:bg-gray-100"
              >
                <Check
                  className={cn(
                    "mt-0.5 h-4 w-4 flex-none",
                    value === client.id ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="flex flex-col overflow-hidden">
                  <span className="truncate font-medium">{client.name}</span>
                  {client.tax_id ? (
                    <span className="truncate text-xs text-muted-foreground">
                      RUT: {client.tax_id}
                    </span>
                  ) : null}
                </span>
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              No se encontraron clientes
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
